import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Search, FileText, Folder, Loader2, Copy, Check, Plus, ArrowRight } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useLocation } from 'react-router-dom'
import { useCommandStore } from '@/stores/useCommandStore'
import { useSearch } from '@/features/search/hooks/useSearch'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { useModifierKey } from '@/hooks/useOS'
import { useRecentSearches } from '@/hooks/useRecentSearches'
import { cn } from '@/lib/utils'
import type { SearchResult } from '@/types/search'

type SourceFilter = 'all' | 'plane' | 'drive'
type Section = 'filters' | 'quickActions' | 'recentSearches' | 'results'

const sourceIcons: Record<string, typeof FileText> = {
  plane: FileText,
  drive: Folder,
}

const sourceLabels: Record<string, string> = {
  plane: 'Plane',
  drive: 'Drive',
}

const filterLabels: { filter: SourceFilter; label: string; icon: typeof FileText }[] = [
  { filter: 'all', label: 'All', icon: Search },
  { filter: 'plane', label: 'Plane', icon: FileText },
  { filter: 'drive', label: 'Drive', icon: Folder },
]

interface QuickAction {
  id: string
  label: string
  description: string
  icon: typeof Plus
  target: string
}

const quickActions: QuickAction[] = [
  {
    id: 'create-project',
    label: 'Connect Plane project',
    description: 'Open the Plane project connection dialog',
    icon: Plus,
    target: 'createProject',
  },
]

interface CommandPaletteProps {
  onOpenCreateProject?: () => void
}

export function CommandPalette({ onOpenCreateProject }: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const location = useLocation()
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [activeSection, setActiveSection] = useState<Section>('quickActions')
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all')
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)

  const { isOpen, close } = useCommandStore()
  const currentProjectId = useMemo(() => {
    const match = location.pathname.match(/^\/projects\/([^/]+)$/)
    return match?.[1]
  }, [location.pathname])

  const { data, isLoading } = useSearch(query, currentProjectId)
  const { symbol: modSymbol } = useModifierKey()
  const { recentSearches, addRecentSearch, removeRecentSearch } = useRecentSearches()

  useKeyboardShortcuts()

  const results = useMemo(() => {
    const allResults = Array.isArray(data?.results) ? data.results : []
    if (sourceFilter === 'all') {
      return allResults
    }
    return allResults.filter((result) => result.source === sourceFilter)
  }, [data, sourceFilter])

  const hasQuery = query.length >= 2
  const hasResults = results.length > 0
  const hasRecentSearches = recentSearches.length > 0 && !hasQuery

  const getSectionItemCount = useCallback(
    (section: Section): number => {
      switch (section) {
        case 'filters':
          return filterLabels.length
        case 'quickActions':
          return hasQuery ? 0 : quickActions.length
        case 'recentSearches':
          return hasRecentSearches ? recentSearches.length : 0
        case 'results':
          return hasResults ? results.length : 0
        default:
          return 0
      }
    },
    [hasQuery, hasRecentSearches, hasResults, recentSearches.length, results.length]
  )

  const availableSections = useMemo((): Section[] => {
    if (!hasQuery) {
      return hasRecentSearches ? ['quickActions', 'recentSearches'] : ['quickActions']
    }

    return hasResults ? ['filters', 'results'] : ['filters']
  }, [hasQuery, hasRecentSearches, hasResults])

  const groupedResults = useMemo(() => {
    return results.reduce(
      (acc, result) => {
        if (!acc[result.source]) {
          acc[result.source] = []
        }
        acc[result.source].push(result)
        return acc
      },
      {} as Record<string, SearchResult[]>
    )
  }, [results])

  const getFlatResultIndex = useCallback(
    (source: string, itemIndex: number): number => {
      let flatIndex = 0
      for (const [group, items] of Object.entries(groupedResults)) {
        if (group === source) {
          return flatIndex + itemIndex
        }
        flatIndex += items.length
      }
      return itemIndex
    },
    [groupedResults]
  )

  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setSelectedIndex(0)
      setSourceFilter('all')
      setActiveSection('quickActions')
    }
  }, [isOpen])

  useEffect(() => {
    if (hasQuery) {
      setActiveSection('results')
      setSelectedIndex(0)
    } else {
      setActiveSection('quickActions')
      setSelectedIndex(0)
    }
  }, [hasQuery])

  const executeQuickAction = useCallback(
    (action: QuickAction) => {
      close()
      if (action.target === 'createProject' && onOpenCreateProject) {
        setTimeout(() => {
          onOpenCreateProject()
        }, 100)
      }
    },
    [close, onOpenCreateProject]
  )

  const handleRecentSearchSelect = useCallback((searchQuery: string) => {
    setQuery(searchQuery)
    inputRef.current?.focus()
  }, [])

  const handleSelect = useCallback(
    (result: SearchResult) => {
      addRecentSearch(query)
      window.open(result.url, '_blank')
      close()
    },
    [addRecentSearch, close, query]
  )

  const copyAsMarkdownLink = useCallback((result: SearchResult, event: React.MouseEvent) => {
    event.stopPropagation()
    navigator.clipboard.writeText(`[${result.title}](${result.url})`)
    setCopiedUrl(result.url)
    setTimeout(() => setCopiedUrl(null), 2000)
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const currentSectionIndex = availableSections.indexOf(activeSection)
      const itemCount = getSectionItemCount(activeSection)

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (selectedIndex < itemCount - 1) {
          setSelectedIndex((index) => index + 1)
        } else if (currentSectionIndex < availableSections.length - 1) {
          setActiveSection(availableSections[currentSectionIndex + 1])
          setSelectedIndex(0)
        }
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (selectedIndex > 0) {
          setSelectedIndex((index) => index - 1)
        } else if (currentSectionIndex > 0) {
          const previousSection = availableSections[currentSectionIndex - 1]
          setActiveSection(previousSection)
          setSelectedIndex(getSectionItemCount(previousSection) - 1)
        }
        return
      }

      if (event.key === 'Tab') {
        event.preventDefault()
        if (event.shiftKey) {
          if (currentSectionIndex > 0) {
            setActiveSection(availableSections[currentSectionIndex - 1])
            setSelectedIndex(0)
          }
        } else if (currentSectionIndex < availableSections.length - 1) {
          setActiveSection(availableSections[currentSectionIndex + 1])
          setSelectedIndex(0)
        }
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        if (activeSection === 'quickActions' && quickActions[selectedIndex]) {
          executeQuickAction(quickActions[selectedIndex])
          return
        }
        if (activeSection === 'recentSearches' && recentSearches[selectedIndex]) {
          handleRecentSearchSelect(recentSearches[selectedIndex])
          return
        }
        if (activeSection === 'results' && results[selectedIndex]) {
          handleSelect(results[selectedIndex])
          return
        }
        if (activeSection === 'filters') {
          setSourceFilter(filterLabels[selectedIndex].filter)
          setActiveSection('results')
          setSelectedIndex(0)
        }
        return
      }

      if (event.key === 'c' && (event.metaKey || event.ctrlKey) && activeSection === 'results' && results[selectedIndex]) {
        event.preventDefault()
        const result = results[selectedIndex]
        navigator.clipboard.writeText(`[${result.title}](${result.url})`)
        setCopiedUrl(result.url)
        setTimeout(() => setCopiedUrl(null), 2000)
      }
    },
    [
      activeSection,
      availableSections,
      executeQuickAction,
      getSectionItemCount,
      handleRecentSearchSelect,
      handleSelect,
      recentSearches,
      results,
      selectedIndex,
    ]
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent className="border border-border bg-background p-0 shadow-xl sm:max-w-3xl">
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search Plane and Drive..."
              className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
            />
            {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-2">
          {!hasQuery && (
            <>
              <div className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Quick Actions
              </div>
              <div className="space-y-1">
                {quickActions.map((action, index) => {
                  const Icon = action.icon
                  const isSelected = activeSection === 'quickActions' && selectedIndex === index
                  return (
                    <button
                      key={action.id}
                      onClick={() => executeQuickAction(action)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors',
                        isSelected ? 'bg-secondary text-foreground' : 'hover:bg-secondary/60'
                      )}
                    >
                      <Icon className="h-4 w-4 text-monokai-blue" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium">{action.label}</p>
                        <p className="text-xs text-muted-foreground">{action.description}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  )
                })}
              </div>

              {hasRecentSearches && (
                <>
                  <div className="mt-4 px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Recent Searches
                  </div>
                  <div className="space-y-1">
                    {recentSearches.map((searchQuery, index) => {
                      const isSelected = activeSection === 'recentSearches' && selectedIndex === index
                      return (
                        <div
                          key={searchQuery}
                          className={cn(
                            'flex items-center gap-2 rounded-md px-3 py-2 transition-colors',
                            isSelected ? 'bg-secondary text-foreground' : 'hover:bg-secondary/60'
                          )}
                        >
                          <button
                            onClick={() => handleRecentSearchSelect(searchQuery)}
                            className="flex min-w-0 flex-1 items-center gap-2 text-left"
                          >
                            <Search className="h-4 w-4 text-muted-foreground" />
                            <span className="truncate text-sm">{searchQuery}</span>
                          </button>
                          <button
                            onClick={() => removeRecentSearch(searchQuery)}
                            className="rounded p-1 text-muted-foreground hover:bg-secondary"
                            aria-label={`Remove ${searchQuery}`}
                          >
                            <Check className="h-3 w-3" />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {hasQuery && (
            <>
              <div className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Filters
              </div>
              <div className="mb-3 flex gap-2 px-2">
                {filterLabels.map((filter, index) => {
                  const Icon = filter.icon
                  const isSelected = sourceFilter === filter.filter
                  const isKeyboardSelected = activeSection === 'filters' && selectedIndex === index
                  return (
                    <button
                      key={filter.filter}
                      onClick={() => setSourceFilter(filter.filter)}
                      className={cn(
                        'flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
                        isSelected ? 'border-monokai-blue bg-monokai-blue/10 text-monokai-blue' : 'border-border',
                        isKeyboardSelected && 'ring-2 ring-monokai-blue/40'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {filter.label}
                    </button>
                  )
                })}
              </div>

              <div className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Results
              </div>
              {results.length === 0 ? (
                <div className="px-3 py-6 text-sm text-muted-foreground">No results found.</div>
              ) : (
                Object.entries(groupedResults).map(([source, items]) => (
                  <div key={source} className="mb-3">
                    <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                      {sourceLabels[source] || source}
                    </div>
                    <div className="space-y-1">
                      {items.map((result, itemIndex) => {
                        const Icon = sourceIcons[result.source] || Search
                        const flatIndex = getFlatResultIndex(source, itemIndex)
                        const isSelected = activeSection === 'results' && selectedIndex === flatIndex
                        return (
                          <div
                            key={`${result.source}-${result.url}`}
                            className={cn(
                              'flex items-center gap-3 rounded-md px-3 py-2 transition-colors',
                              isSelected ? 'bg-secondary text-foreground' : 'hover:bg-secondary/60'
                            )}
                          >
                            <button
                              onClick={() => handleSelect(result)}
                              className="flex min-w-0 flex-1 items-center gap-3 text-left"
                            >
                              <Icon className="h-4 w-4 text-monokai-blue" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium">{result.title}</p>
                                {result.subtitle && (
                                  <p className="truncate text-xs text-muted-foreground">{result.subtitle}</p>
                                )}
                              </div>
                            </button>
                            <button
                              onClick={(event) => copyAsMarkdownLink(result, event)}
                              className="rounded p-1 text-muted-foreground hover:bg-secondary"
                              aria-label={`Copy markdown link for ${result.title}`}
                            >
                              {copiedUrl === result.url ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>

        <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          {modSymbol}+K to open, Enter to select, {modSymbol}+C to copy markdown link
        </div>
      </DialogContent>
    </Dialog>
  )
}
